import React, { useContext, useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import './Layout.css';

const Layout = ({ children }) => {
  const { logout } = useContext(AuthContext);
  const { companyName, logoUrl } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/sy-admin/login');
  };

  const isActive = (path) => {
    if (path === '/sy-admin' || path === '/sy-admin/') {
      return location.pathname === '/sy-admin' || location.pathname === '/sy-admin/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="layout">
      {sidebarOpen && <div 
        className="sidebar-overlay"
        onClick={() => setSidebarOpen(false)}
        aria-label="Close sidebar"
      />}
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <div className="sidebar-brand">
            {logoUrl ? (
              <img src={logoUrl} alt={companyName} className="brand-logo" />
            ) : (
              <span className="brand-icon">🏨</span>
            )}
            {sidebarOpen && <span className="brand-text">{companyName}</span>}
          </div>
          <button 
            className="sidebar-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label="Toggle sidebar"
          >
            {sidebarOpen ? '←' : '→'}
          </button>
        </div>
        
        <nav className="sidebar-nav">
          <Link 
            to="/sy-admin" 
            className={`nav-item ${isActive('/sy-admin') && (location.pathname === '/sy-admin' || location.pathname === '/sy-admin/') ? 'active' : ''}`}
            title="Dashboard"
            onClick={() => window.innerWidth < 768 && setSidebarOpen(false)}
          >
            <span className="nav-icon">📊</span>
            {sidebarOpen && <span className="nav-text">Dashboard</span>}
          </Link>
          <Link 
            to="/sy-admin/hotels" 
            className={`nav-item ${isActive('/sy-admin/hotels') ? 'active' : ''}`}
            title="Hotels"
            onClick={() => window.innerWidth < 768 && setSidebarOpen(false)}
          >
            <span className="nav-icon">🏢</span>
            {sidebarOpen && <span className="nav-text">Hotels</span>}
          </Link>
          <Link 
            to="/sy-admin/spas" 
            className={`nav-item ${isActive('/sy-admin/spas') ? 'active' : ''}`}
            title="Spas"
            onClick={() => window.innerWidth < 768 && setSidebarOpen(false)}
          >
            <span className="nav-icon">💆</span>
            {sidebarOpen && <span className="nav-text">Spas</span>}
          </Link>
          <Link 
            to="/sy-admin/tours" 
            className={`nav-item ${isActive('/sy-admin/tours') ? 'active' : ''}`}
            title="Tours & Activities"
            onClick={() => window.innerWidth < 768 && setSidebarOpen(false)}
          >
            <span className="nav-icon">🎫</span>
            {sidebarOpen && <span className="nav-text">Tours</span>}
          </Link>
          <Link 
            to="/sy-admin/travel-packages" 
            className={`nav-item ${isActive('/sy-admin/travel-packages') ? 'active' : ''}`}
            title="Travel Packages"
            onClick={() => window.innerWidth < 768 && setSidebarOpen(false)}
          >
            <span className="nav-icon">✈️</span>
            {sidebarOpen && <span className="nav-text">Travel Packages</span>}
          </Link>
          <Link 
            to="/sy-admin/facilities" 
            className={`nav-item ${isActive('/sy-admin/facilities') ? 'active' : ''}`}
            title="Facilities"
            onClick={() => window.innerWidth < 768 && setSidebarOpen(false)}
          >
            <span className="nav-icon">🏗️</span>
            {sidebarOpen && <span className="nav-text">Facilities</span>}
          </Link>
          <Link 
            to="/sy-admin/amenities" 
            className={`nav-item ${isActive('/sy-admin/amenities') ? 'active' : ''}`}
            title="Amenities"
            onClick={() => window.innerWidth < 768 && setSidebarOpen(false)}
          >
            <span className="nav-icon">✨</span>
            {sidebarOpen && <span className="nav-text">Amenities</span>}
          </Link>
          <Link 
            to="/sy-admin/hotel-categories" 
            className={`nav-item ${isActive('/sy-admin/hotel-categories') ? 'active' : ''}`}
            title="Hotel Categories"
            onClick={() => window.innerWidth < 768 && setSidebarOpen(false)}
          >
            <span className="nav-icon">🏷️</span>
            {sidebarOpen && <span className="nav-text">Hotel Categories</span>}
          </Link>
          <Link 
            to="/sy-admin/customers" 
            className={`nav-item ${isActive('/sy-admin/customers') ? 'active' : ''}`}
            title="Customers"
            onClick={() => window.innerWidth < 768 && setSidebarOpen(false)}
          >
            <span className="nav-icon">👥</span>
            {sidebarOpen && <span className="nav-text">Customers</span>}
          </Link>
          <Link 
            to="/sy-admin/wishlists" 
            className={`nav-item ${isActive('/sy-admin/wishlists') ? 'active' : ''}`}
            title="Wishlists"
            onClick={() => window.innerWidth < 768 && setSidebarOpen(false)}
          >
            <span className="nav-icon">❤️</span>
            {sidebarOpen && <span className="nav-text">Wishlists</span>}
          </Link>
          <Link 
            to="/sy-admin/push-notifications" 
            className={`nav-item ${isActive('/sy-admin/push-notifications') ? 'active' : ''}`}
            title="Push Notifications"
            onClick={() => window.innerWidth < 768 && setSidebarOpen(false)}
          >
            <span className="nav-icon">🔔</span>
            {sidebarOpen && <span className="nav-text">Notifications</span>}
          </Link>
          <Link 
            to="/sy-admin/suggested-destinations" 
            className={`nav-item ${isActive('/sy-admin/suggested-destinations') ? 'active' : ''}`}
            title="Suggested Destinations"
            onClick={() => window.innerWidth < 768 && setSidebarOpen(false)}
          >
            <span className="nav-icon">📍</span>
            {sidebarOpen && <span className="nav-text">Destinations</span>}
          </Link>
          <Link 
            to="/sy-admin/legal-pages" 
            className={`nav-item ${isActive('/sy-admin/legal-pages') ? 'active' : ''}`}
            title="Legal Pages"
            onClick={() => window.innerWidth < 768 && setSidebarOpen(false)}
          >
            <span className="nav-icon">📄</span>
            {sidebarOpen && <span className="nav-text">Legal Pages</span>}
          </Link>
          <Link 
            to="/sy-admin/verification-subscriptions" 
            className={`nav-item ${isActive('/sy-admin/verification-subscriptions') ? 'active' : ''}`}
            title="Verification Subscriptions"
            onClick={() => window.innerWidth < 768 && setSidebarOpen(false)}
          >
            <span className="nav-icon">✓</span>
            {sidebarOpen && <span className="nav-text">Verification Subs</span>}
          </Link>
          <Link 
            to="/sy-admin/featured-subscriptions" 
            className={`nav-item ${isActive('/sy-admin/featured-subscriptions') ? 'active' : ''}`}
            title="Featured Subscriptions"
            onClick={() => window.innerWidth < 768 && setSidebarOpen(false)}
          >
            <span className="nav-icon">⭐</span>
            {sidebarOpen && <span className="nav-text">Featured Subs</span>}
          </Link>
          <Link 
            to="/sy-admin/subscription-history" 
            className={`nav-item ${isActive('/sy-admin/subscription-history') ? 'active' : ''}`}
            title="Subscription History"
            onClick={() => window.innerWidth < 768 && setSidebarOpen(false)}
          >
            <span className="nav-icon">📋</span>
            {sidebarOpen && <span className="nav-text">Subscription History</span>}
          </Link>
          <Link 
            to="/sy-admin/settings" 
            className={`nav-item ${isActive('/sy-admin/settings') ? 'active' : ''}`}
            title="Settings"
            onClick={() => window.innerWidth < 768 && setSidebarOpen(false)}
          >
            <span className="nav-icon">⚙️</span>
            {sidebarOpen && <span className="nav-text">Settings</span>}
          </Link>
        </nav>

        <div className="sidebar-footer">
          <button 
            onClick={handleLogout} 
            className="nav-item logout-btn"
            title="Logout"
          >
            <span className="nav-icon">🚪</span>
            {sidebarOpen && <span className="nav-text">Logout</span>}
          </button>
        </div>
      </aside>

      <main className={`main-content ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        {!sidebarOpen && (
          <button 
            className="mobile-menu-btn"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open menu"
          >
            ☰
          </button>
        )}
        <div className="content-wrapper">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;

